<template>
  <nav class="navigation-bar" :class="{'navigation-bar--editor': styles.showEditor && !revisionContent, 'navigation-bar--light': light}">
    <!-- Explorer -->
    <div class="navigation-bar__inner navigation-bar__inner--left navigation-bar__inner--button">
      <button class="navigation-bar__button navigation-bar__button--close button" v-if="light" @click="close()" v-title="'Close Notes'"><icon-check-circle></icon-check-circle></button>
      <button class="navigation-bar__button navigation-bar__button--explorer-toggler button" v-else tour-step-anchor="explorer" @click="toggleExplorer()" v-title="'Toggle explorer'"><icon-folder></icon-folder></button>
    </div>
    <!-- Side bar -->
    <div class="navigation-bar__inner navigation-bar__inner--right navigation-bar__inner--button">
      <a class="navigation-bar__button navigation-bar__button--notes button" v-if="light" href="app" target="_blank" v-title="'Open Notes'"><icon-provider provider-id="notes"></icon-provider></a>
      <button class="navigation-bar__button navigation-bar__button--notes button" v-else tour-step-anchor="menu" @click="toggleSideBar()" v-title="'Toggle side bar'"><icon-provider provider-id="notes"></icon-provider></button>
    </div>
    <!-- TOC -->
    <div class="navigation-bar__inner navigation-bar__inner--right navigation-bar__inner--button" v-if="!light">
      <button class="navigation-bar__button button" @click="toggleToc()" v-title="'Table of contents'"><icon-toc></icon-toc></button>
    </div>
    <div class="navigation-bar__inner navigation-bar__inner--right navigation-bar__inner--title flex flex--row">
      <!-- Title -->
      <div class="navigation-bar__title navigation-bar__title--fake text-input"></div>
      <div class="navigation-bar__title navigation-bar__title--text text-input" :style="{width: titleWidth + 'px'}">{{title}}</div>
      <input class="navigation-bar__title navigation-bar__title--input text-input" :class="{'navigation-bar__title--focus': titleFocus, 'navigation-bar__title--scrolling': titleScrolling}" :style="{width: titleWidth + 'px'}" @focus="editTitle(true)" @blur="editTitle(false)" @keydown.enter="submitTitle(false)" @keydown.esc.stop="submitTitle(true)" @mouseenter="titleHover = true" @mouseleave="titleHover = false" v-model="title">
      <!-- Revision -->
      <div class="flex flex--row" v-if="revisionContent">
        <button class="navigation-bar__button navigation-bar__button--notes navigation-bar__button--restore button" @click="restoreRevision">Restore</button>
        <button class="navigation-bar__button navigation-bar__button--notes button" @click="setRevisionContent()" v-title="'Close revision'"><icon-close></icon-close></button>
      </div>
    </div>
    <div class="navigation-bar__inner navigation-bar__inner--edit-pagedownButtons">
      <button class="navigation-bar__button button" @click="undo" v-title="'Undo'" :disabled="!canUndo"><icon-undo></icon-undo></button>
      <button class="navigation-bar__button button" @click="redo" v-title="'Redo'" :disabled="!canRedo"><icon-redo></icon-redo></button>
      <div v-for="button in pagedownButtons" :key="button.method">
        <button class="navigation-bar__button button" v-if="button.method" @click="pagedownClick(button.method)" v-title="button.titleWithShortcut">
          <component :is="button.iconClass"></component>
        </button>
        <div class="navigation-bar__spacer" v-else></div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import editorSvc from '../services/editorSvc';
import animationSvc from '../services/animationSvc';
import tempFileSvc from '../services/tempFileSvc';
import utils from '../services/utils';
import pagedownButtons from '../data/pagedownButtons';
import store from '../store';
import workspaceSvc from '../services/workspaceSvc';

// According to mousetrap
const mod = /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? 'Meta' : 'Ctrl';

const getShortcut = (method) => {
  let result = '';
  const shortcuts = store.getters['data/computedSettings']?.shortcuts;
  if (!shortcuts) return result;
  Object.entries(shortcuts).some(([keys, shortcut]) => {
    if (`${shortcut.method || shortcut}` === method) {
      result = keys.split('+').map(key => key.toLowerCase()).map((key) => {
        if (key === 'mod') {
          return mod;
        }
        // Capitalize
        return key && `${key[0].toUpperCase()}${key.slice(1)}`;
      }).join('+');
    }
    return result;
  });
  return result && ` – ${result}`;
};

export default {
  data: () => ({
    mounted: false,
    title: '',
    titleFocus: false,
    titleHover: false,
  }),
  computed: {
    ...mapState([
      'light',
      'offline',
    ]),
    ...mapState('layout', [
      'canUndo',
      'canRedo',
    ]),
    ...mapState('content', [
      'revisionContent',
    ]),
    ...mapGetters('layout', [
      'styles',
    ]),
    pagedownButtons() {
      return pagedownButtons.map(button => ({
        ...button,
        titleWithShortcut: `${button.title}${getShortcut(button.method)}`,
        iconClass: `icon-${button.icon}`,
      }));
    },
    showSpinner() {
      return !store.state.queue.isEmpty;
    },
    titleWidth() {
      if (!this.mounted) {
        return 200; // Default width
      }
      const { titleMaxWidth } = this.styles;
      // Always use full available width for consistent sizing
      return titleMaxWidth;
    },
    titleScrolling() {
      if (!this.mounted) return false;
      const result = this.titleHover && !this.titleFocus;
      if (this.titleInputElt) {
        if (result) {
          const scrollLeft = this.titleInputElt.scrollWidth - this.titleInputElt.offsetWidth;
          animationSvc.animate(this.titleInputElt)
            .scrollLeft(scrollLeft)
            .duration(scrollLeft * 10)
            .easing('inOut')
            .start();
        } else {
          animationSvc.animate(this.titleInputElt)
            .scrollLeft(0)
            .start();
        }
      }
      return result;
    },
    editCancelTrigger() {
      const current = store.getters['file/current'];
      return utils.serializeObject([
        current.id,
        current.name,
      ]);
    },
  },
  methods: {
    ...mapMutations('content', [
      'setRevisionContent',
    ]),
    ...mapActions('content', [
      'restoreRevision',
    ]),
    ...mapActions('data', [
      'toggleExplorer',
      'toggleSideBar',
    ]),
    toggleToc() {
      // Open sidebar with TOC panel
      store.dispatch('data/toggleSideBar', true);
      store.dispatch('data/setSideBarPanel', 'toc');
    },
    undo() {
      return editorSvc.clEditor.undoMgr.undo();
    },
    redo() {
      return editorSvc.clEditor.undoMgr.redo();
    },
    pagedownClick(name) {
      if (store.getters['content/isCurrentEditable']) {
        const text = editorSvc.clEditor.getContent();
        editorSvc.pagedownEditor.uiManager.doClick(name);
        if (text !== editorSvc.clEditor.getContent()) {
        }
      }
    },
    async editTitle(toggle) {
      this.titleFocus = toggle;
      if (toggle) {
        this.titleInputElt.setSelectionRange(0, this.titleInputElt.value.length);
      } else {
        const title = this.title.trim();
        this.title = store.getters['file/current'].name;
        if (title && this.title !== title) {
          try {
            await workspaceSvc.storeItem({
              ...store.getters['file/current'],
              name: title,
            });
          } catch (e) {
            // Cancel
          }
        }
      }
    },
    submitTitle(reset) {
      if (reset) {
        this.title = '';
      }
      this.titleInputElt.blur();
    },
    close() {
      tempFileSvc.close();
    },
  },
  created() {
    this.$watch(
      () => this.editCancelTrigger,
      () => {
        const currentFile = store.getters['file/current'];
        this.title = currentFile?.name || 'Untitled';
        this.editTitle(false);
      },
      { immediate: true },
    );
  },
  mounted() {
    this.titleFakeElt = this.$el.querySelector('.navigation-bar__title--fake');
    this.titleInputElt = this.$el.querySelector('.navigation-bar__title--input');
    this.mounted = true;
  },
};
</script>

<style lang="scss">
@use 'sass:math';
@import '../styles/variables.scss';

.navigation-bar {
  position: absolute;
  width: 100%;
  height: 100%;
  padding-top: 4px;
  overflow: hidden;
}

.navigation-bar__hidden {
  display: none;
}

.navigation-bar__inner--left {
  float: left;

  &.navigation-bar__inner--button {
    margin-right: 12px;
  }
}

.navigation-bar__inner--right {
  float: right;

  /* prevent from seeing wrapped pagedownButtons */
  margin-bottom: 20px;
}

.navigation-bar__inner--button {
  margin: 0 4px;
  display: flex;
  align-items: center;
}

.navigation-bar__inner--edit-pagedownButtons {
  margin-left: 15px;

  .navigation-bar__button,
  .navigation-bar__spacer {
    float: left;
  }
}

.navigation-bar__inner--title {
  flex: 1;
  min-width: 0;
}

.navigation-bar__inner--title > *:not(.navigation-bar__title--input):not(.navigation-bar__title--text) {
  flex: none;
}

.navigation-bar__button,
.navigation-bar__spacer {
  height: 36px;
  padding: 0 4px;

  /* prevent from seeing wrapped pagedownButtons */
  margin-bottom: 20px;
}

.navigation-bar__button {
  width: 34px;
  padding: 0 7px;
  transition: opacity 0.25s;
  display: flex;
  align-items: center;
  justify-content: center;

  &,
  &.button {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  &:active,
  &:focus,
  &:hover {
    &,
    &.button {
      color: #fff !important;
    }
  }

  .app--sepia &,
  .app--sepia &.button {
    color: rgba(91, 70, 54, 0.8) !important;
  }

  .app--sepia &:active,
  .app--sepia &:focus,
  .app--sepia &:hover {
    &,
    &.button {
      color: #5b4636 !important;
    }
  }

  .app--solarized-light &,
  .app--solarized-light &.button {
    color: rgba(88, 110, 117, 0.8) !important;
  }

  .app--solarized-light &:active,
  .app--solarized-light &:focus,
  .app--solarized-light &:hover {
    &,
    &.button {
      color: #586e75 !important;
    }
  }

  .icon,
  .icon-provider {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .navigation-bar__inner--button & {
    padding: 0 8px;
    width: 44px;
    height: 36px;

    .icon,
    .icon-provider {
      width: 24px;
      height: 24px;
    }

    &.navigation-bar__button--notes {
      opacity: 0.85;

      &:active,
      &:focus,
      &:hover {
        opacity: 1;
      }
    }
  }
}

.navigation-bar__button--revision {
  width: 38px;

  &:first-child {
    margin-left: 10px;
  }

  &:last-child {
    margin-right: 10px;
  }
}

.navigation-bar__button--restore {
  width: auto;
}

.navigation-bar__title {
  margin: 0 4px;
  font-size: 21px;

  .layout--revision & {
    position: absolute;
    left: -9999px;
  }
}

.navigation-bar__title,
.navigation-bar__button {
  display: inline-block;
  color: $navbar-color;
  background-color: transparent;
}

.navigation-bar__button--sync,
.navigation-bar__button--publish {
  padding: 0 6px;
  margin: 0 5px;
}

.navigation-bar__button[disabled] {
  &,
  &:active,
  &:focus,
  &:hover {
    color: $navbar-color;
  }
}

.navigation-bar__title--input,
.navigation-bar__button {
  &:active,
  &:focus,
  &:hover {
    color: $navbar-hover-color;
    background-color: $navbar-hover-background;
  }
}

.navigation-bar__button--location {
  width: 20px;
  height: 20px;
  border-radius: 10px;
  padding: 2px;
  margin-top: 8px;
  opacity: 0.5;
  background-color: rgba(255, 255, 255, 0.2);

  &:active,
  &:focus,
  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.2);
  }
}

.navigation-bar__button--blink {
  animation: blink 1s linear infinite;
}

.navigation-bar__title--fake {
  position: absolute;
  left: -9999px;
  width: auto;
  white-space: pre-wrap;
}

.navigation-bar__title--text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 50px;
  display: none;
}

.navigation-bar__title--input,
.navigation-bar__inner--edit-pagedownButtons {
  display: none;
}

// Editor mode: show input, hide text
.navigation-bar--editor {
  .navigation-bar__title--input,
  .navigation-bar__inner--edit-pagedownButtons {
    display: block;
  }
  
  .navigation-bar__title--text {
    display: none !important;
  }
}

// Preview mode (non-editor): show text, hide input
.navigation-bar:not(.navigation-bar--editor) {
  .navigation-bar__inner--title {
    overflow: visible;
    display: flex;
    align-items: center;
  }
  
  .navigation-bar__title--text {
    display: inline-block !important;
  }
  
  .navigation-bar__title--input {
    display: none !important;
  }
}

/* Moved to main .navigation-bar__button block above */

.navigation-bar__button--close {
  color: lighten($link-color, 15%);

  &:active,
  &:focus,
  &:hover {
    color: lighten($link-color, 25%);
  }
}

.navigation-bar__title--input {
  cursor: pointer;

  &.navigation-bar__title--focus {
    cursor: text;
  }

  .navigation-bar--light & {
    display: none;
  }
}

$r: 10px;
$d: $r * 2;
$b: math.div($d, 10);
$t: 3000ms;

.navigation-bar__spinner {
  width: 24px;
  margin: 7px 0 0 8px;

  .icon {
    width: 24px;
    height: 24px;
    color: rgba($error-color, 0.5);
  }
}

.spinner {
  width: $d;
  height: $d;
  display: block;
  position: relative;
  border: $b solid rgba($navbar-color, 0.5);
  border-radius: 50%;
  margin: 2px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    display: block;
    width: $b;
    background-color: $navbar-color;
    border-radius: $b * 0.5;
    transform-origin: 50% 0;
  }

  &::before {
    height: $r * 0.4;
    left: $r - $b * 1.5;
    top: 50%;
    animation: spin $t linear infinite;
  }

  &::after {
    height: $r * 0.6;
    left: $r - $b * 1.5;
    top: 50%;
    animation: spin math.div($t, 4) linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}

// Responsive styles - flexbox layout at 870px when editor buttons move to bottom
@media (max-width: 870px) {
  // Hide editor toolbar - moved to bottom mobile toolbar
  .navigation-bar__inner--edit-pagedownButtons {
    display: none !important;
  }

  // Convert to flexbox layout when editor buttons move to bottom toolbar
  .navigation-bar {
    position: relative;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    padding: 0 4px;
    height: 100%;
    box-sizing: border-box;
    overflow: visible;
  }

  .navigation-bar__inner--left,
  .navigation-bar__inner--right {
    float: none;
    flex-shrink: 0;
    margin-bottom: 0;
  }

  // Reorder: Explorer (1), Title (2), TOC (3), Menu (4)
  // DOM order: Explorer, Menu, TOC, Title
  .navigation-bar__inner--left {
    order: 1;
  }

  .navigation-bar__inner--title {
    flex: 1;
    min-width: 0;
    order: 2;
    overflow: hidden;
    display: flex;
    align-items: center;
  }

  // TOC button (second .navigation-bar__inner--right.navigation-bar__inner--button in DOM)
  .navigation-bar__inner--right.navigation-bar__inner--button {
    order: 4; // Default for menu (first in DOM)
  }

  // Use adjacent sibling to target TOC (comes after menu in DOM)
  .navigation-bar__inner--right.navigation-bar__inner--button + .navigation-bar__inner--right.navigation-bar__inner--button {
    order: 3; // TOC
  }

  .navigation-bar__title {
    font-size: 18px;
    max-width: 100%;
    box-sizing: border-box;
    height: auto;
    line-height: normal;
  }

  .navigation-bar__title--input,
  .navigation-bar__title--text {
    width: 100% !important;
    max-width: 100%;
  }
  
  .navigation-bar__button,
  .navigation-bar__spacer {
    margin-bottom: 0;
    height: 32px;
  }

  .navigation-bar__spinner {
    margin: 0 4px 0 4px;
    display: flex;
    align-items: center;
  }

  .navigation-bar__button {
    width: 36px;
    padding: 0 5px;
    height: 32px;

    .icon,
    .icon-provider {
      width: 18px;
      height: 18px;
    }
  }

  .navigation-bar__inner--button .navigation-bar__button {
    width: 38px;

    .icon,
    .icon-provider {
      width: 22px;
      height: 22px;
    }
  }

  .navigation-bar__spacer {
    padding: 0 2px;
  }
}

@media (max-width: 480px) {
  .navigation-bar__title {
    font-size: 16px;
    margin: 0 2px;
    max-width: 100%;
  }

  .navigation-bar__inner--title {
    min-width: 50px;
  }

  .navigation-bar__button {
    width: 28px;
    padding: 0 4px;

    .icon,
    .icon-provider {
      width: 16px;
      height: 16px;
    }
  }

  .navigation-bar__inner--button .navigation-bar__button {
    width: 34px;
    padding: 0 5px;

    .icon,
    .icon-provider {
      width: 20px;
      height: 20px;
    }
  }

  .navigation-bar__spinner {
    width: 20px;
    margin: 8px 0 0 4px;

    .icon {
      width: 20px;
      height: 20px;
    }
  }
}

// Very small screens - narrow title input and center with button bar
@media (max-width: 360px) {
  .navigation-bar__inner--title {
    flex: 1;
    justify-content: center;
    padding-right: 36px; // Account for button bar overlay width
  }

  .navigation-bar__title--input,
  .navigation-bar__title--text {
    max-width: 200px !important;
    width: auto !important;
    text-align: center;
  }

  .navigation-bar__title {
    font-size: 14px;
  }
}
</style>
